import React, { useState } from 'react'
import * as yup from 'yup'
import InputMask from 'react-input-mask'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import { useIntl } from 'react-intl'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogTitle from '@mui/material/DialogTitle'
import {
    Box,
    CircularProgress,
    DialogContent,
    FormHelperText,
    IconButton,
    SelectChangeEvent,
} from '@mui/material'
import EditIcon from '../../../../assets/icons/Edit/Edit.svg'
import {
    email,
    requiredMobile,
    requiredString,
} from '../../../../utils/validation'
import { H6 } from '../../../../layouts/TextStyles'
import CustomInput from '../../../../components/muiComponents/customInput'
import MultipleSelect from '../../../../components/muiComponents/multipleSelect/MultipleSelect'
import { teacherApiSlice } from '../../../../features/teacher/teacherSlice'
import { subjectApiSlice } from '../../../../features/subject/subjectSlice'
import SingleSelect from '../../../../components/muiComponents/singleSelect'
import { convertToMobile } from '../../../../utils/functions'
import { TeacherState } from '../../../../features/types'
import FileUploadIcon from '@mui/icons-material/FileUpload'

const styles = {
    error: {
        color: '#d32f2f',
        marginLeft: 2,
    },
}

interface Props {
    editTeacher?: TeacherState
    editMode?: boolean
}

const AddTeacherDialog = ({ editTeacher, editMode }: Props) => {
    const { formatMessage } = useIntl()
    const [open, setOpen] = useState(false)
    const { data: subjects = [] } = subjectApiSlice.useFetchAllSubjectsQuery()
    const [photoPreview, setPhotoPreview] = useState<string | null>(null)
    const [createTeacher, { isLoading: isCreating }] =
        teacherApiSlice.useCreateTeacherMutation()
    const [updateTeacher, { isLoading: isUpdating }] =
        teacherApiSlice.useUpdateTeacherMutation()

    const teacher: TeacherState = {
        id: editTeacher?.id || null,
        firstName: editTeacher?.firstName || '',
        lastName: editTeacher?.lastName || '',
        email: editTeacher?.email || '',
        mobile: editTeacher?.mobile || '',
        role: editTeacher?.role || '',
        subjectIds: editTeacher?.subjects?.map((item) => item.id) || [],
        photo: editTeacher?.photo || '',
    }

    const validationSchema = yup.object().shape(
        {
            firstName: requiredString({ formatMessage }),
            lastName: requiredString({ formatMessage }),
            email: yup
                .string()
                .ensure()
                .when('mobile', {
                    is: '',
                    then: email({ formatMessage }),
                }),
            mobile: yup
                .string()
                .ensure()
                .when('email', {
                    is: '',
                    then: requiredMobile({ formatMessage }),
                }),
            role: requiredString({ formatMessage }),
        },
        [['email', 'mobile']],
    )

    const formik = useFormik({
        initialValues: teacher,
        validationSchema: validationSchema,
        validateOnChange: false,
        validateOnBlur: false,

        onSubmit: async () => {
            if (editTeacher) {
                handleUpdateTeacher()
            } else {
                handleCreateTeacher()
            }
        },
    })

    const currentPhotoSrc =
        (photoPreview as string | null) ||
        (typeof (formik as any)?.values?.photo === 'string'
            ? (formik as any).values.photo
            : '') ||
        (editTeacher?.photo || '')

    const photoLabel = (() => {
        const photoVal: any = (formik as any)?.values?.photo
        if (!photoVal) return ''
        if (typeof photoVal === 'string') return photoVal
        return photoVal?.title || photoVal?.name || ''
    })()

    const buildFormData = (values: typeof formik.values) => {
        const formData = new FormData()
        if (values.id !== undefined && values.id !== null) {
            formData.append('id', String(values.id))
        }
        formData.append('firstName', values.firstName || '')
        formData.append('lastName', values.lastName || '')
        formData.append('email', values.email || '')
        formData.append('mobile', convertToMobile(values.mobile || ''))
        formData.append('role', values.role || '')
        if (Array.isArray(values.subjectIds)) {
            values.subjectIds.forEach((id: any) => {
                formData.append('subjectIds', String(Number(id)))
            })
        }
        const photoVal: any = (values as any).photo
        if (photoVal && typeof photoVal !== 'string') {
            formData.append('photo', photoVal)
        }
        return formData
    }

    const handleCreateTeacher = async () => {
        try {
            const formData = buildFormData(formik.values)
            await createTeacher(formData).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            handleClose()
        } catch (error) { }
    }

    const handleUpdateTeacher = async () => {
        try {
            const formData = buildFormData(formik.values)
            if (formik.values.id !== undefined && formik.values.id !== null) {
                formData.set('id', String(formik.values.id))
            }
            await updateTeacher(formData).unwrap()
            toast.success(formatMessage({ id: 'updatedSuccess' }))
            handleClose()
        } catch (error) { }
    }

    const handleRoleChange = (
        event: SelectChangeEvent<typeof formik.values.role>,
    ) => {
        const {
            target: { value },
        } = event

        if (value === 'CURATOR') {
            formik.setFieldValue('subjectIds', [])
        }
        formik.setFieldValue('role', value)
    }

    const handleSubjectsChange = (
        event: SelectChangeEvent<typeof formik.values.subjectIds>,
    ) => {
        const {
            target: { value },
        } = event

        const nextValue =
            typeof value === 'string'
                ? value.split(',').map((v) => Number(v))
                : (value as any[]).map((v) => Number(v))
        formik.setFieldValue('subjectIds', nextValue)
    }

    const handleClickOpen = () => {
        setOpen(true)
        formik.setValues(teacher)
    }

    const handleClose = () => {
        setOpen(false)
        setPhotoPreview(null)
        formik.resetForm()
    }

    const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.[0]?.size > 10 * 1024 * 1024) {
            toast.error(formatMessage({ id: 'filesDescription' }))
            return
        }
        const file = e.target.files?.[0]
        if (file) {
            formik.setFieldValue('photo', file)
            setPhotoPreview(URL.createObjectURL(file))
        }
    }

    return (
        <div>
            {editMode ? (
                <IconButton
                    sx={{ background: '#f3f4f5' }}
                    onClick={handleClickOpen}
                >
                    <img
                        src={EditIcon}
                        alt="edit"
                    />
                </IconButton>
            ) : (
                <Button
                    variant={'contained'}
                    onClick={handleClickOpen}
                >
                    {formatMessage({ id: 'addTeacher' })}
                </Button>
            )}

            <Dialog
                maxWidth={'md'}
                open={open}
                onClose={handleClose}
                fullWidth
            >
                <DialogTitle id="responsive-dialog-title">
                    {editMode
                        ? formatMessage({ id: 'editData' })
                        : formatMessage({ id: 'addTeacher' })}
                </DialogTitle>

                <DialogContent>
                    <form onSubmit={formik.handleSubmit}>
                        <Box mb={2}>
                            <H6>{formatMessage({ id: 'firstName' })}</H6>

                            <CustomInput
                                id="firstName"
                                name="firstName"
                                placeholder={formatMessage({ id: 'firstName' })}
                                fullWidth
                                value={formik.values.firstName}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                helperText={formik.errors.firstName}
                                error={Boolean(formik.errors.firstName)}
                                margin="dense"
                            />
                        </Box>

                        <Box mb={2}>
                            <H6>{formatMessage({ id: 'lastName' })}</H6>

                            <CustomInput
                                id="lastName"
                                name="lastName"
                                placeholder={formatMessage({ id: 'lastName' })}
                                fullWidth
                                value={formik.values.lastName}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.errors.lastName)}
                                helperText={formik.errors.lastName}
                                margin="dense"
                            />
                        </Box>

                        <Box mb={2}>
                            <H6>{formatMessage({ id: 'email' })}</H6>

                            <CustomInput
                                id="email"
                                name="email"
                                type="mail"
                                placeholder={formatMessage({ id: 'email' })}
                                fullWidth
                                value={formik.values.email}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.errors.email)}
                                helperText={formik.errors.email}
                                margin="dense"
                            />
                        </Box>

                        <Box mb={2}>
                            <H6>{formatMessage({ id: 'mobile' })}</H6>

                            <InputMask
                                mask="+7 (999) 999-99-99"
                                value={formik.values.mobile}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                            >
                                <CustomInput
                                    id="mobile"
                                    name="mobile"
                                    placeholder={formatMessage({
                                        id: 'enterPhone',
                                    })}
                                    fullWidth
                                    helperText={formik.errors.mobile}
                                    error={Boolean(formik.errors.mobile)}
                                    margin="dense"
                                />
                            </InputMask>
                        </Box>

                        <Box mb={2}>
                            <H6>{formatMessage({ id: 'roles' })}</H6>

                            <SingleSelect
                                id="role"
                                name="role"
                                placeholder={formatMessage({ id: 'roles' })}
                                value={formik.values.role}
                                handleChange={handleRoleChange}
                                handleBlur={formik.handleBlur}
                                menuItems={[
                                    {
                                        id: 'TEACHER',
                                        name: formatMessage({ id: 'teacher' }),
                                    },
                                    {
                                        id: 'CURATOR',
                                        name: formatMessage({ id: 'curator' }),
                                    },
                                ]}
                                handleError={Boolean(formik.errors.role)}
                            />

                            <FormHelperText sx={styles.error}>
                                {formik.touched.role && formik.errors.role}
                            </FormHelperText>
                        </Box>

                        <Box mb={2}>
                            <H6>{formatMessage({ id: 'subjects' })}</H6>

                            <MultipleSelect
                                id="subjectIds"
                                name="subjectIds"
                                multiple
                                placeholder={formatMessage({ id: 'subjects' })}
                                value={formik.values.subjectIds}
                                disabled={formik.values.role === 'CURATOR'}
                                handleChange={handleSubjectsChange}
                                menuItems={subjects}
                            />
                        </Box>
                        <Box mb={2}>
                            <H6>{formatMessage({ id: 'portraitPhoto' })}</H6>
                            <Button
                                component="label"
                                variant="outlined"
                                color="primary"
                                startIcon={<FileUploadIcon />}
                            >
                                Загрузить фото
                                <input
                                    type="file"
                                    hidden
                                    accept="image/*"
                                    onChange={handlePhotoChange}
                                />
                            </Button>

                            <FormHelperText style={styles.error}>
                                {formik.touched.photo && (formik.errors as any)?.photo}
                            </FormHelperText>

                            {currentPhotoSrc && (
                                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <img
                                        src={currentPhotoSrc}
                                        alt="preview"
                                        style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8 }}
                                    />
                                    {photoLabel && <span>{photoLabel}</span>}
                                </Box>
                            )}
                        </Box>
                    </form>
                </DialogContent>

                <DialogActions sx={{ m: 2, mt: 0 }}>
                    <Button
                        onClick={handleClose}
                        variant={'outlined'}
                        sx={{ mr: 1 }}
                        disabled={isCreating || isUpdating}
                    >
                        {formatMessage({ id: 'cancel' })}
                    </Button>

                    <Button
                        variant={'contained'}
                        sx={{ width: '120px' }}
                        disabled={isCreating || isUpdating}
                        onClick={() => formik.handleSubmit()}
                    >
                        {isCreating || isUpdating ? (
                            <CircularProgress
                                size={20}
                                style={{ marginRight: '8px' }}
                            />
                        ) : (
                            formatMessage({ id: 'save' })
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

export default AddTeacherDialog
